import { Create } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import {
  ResponseMessages,
  SupportCreateTicket,
} from '../../../types/commonTypes';
import { Modal, ModalContent } from '../../../components/Modal';
import { TicketContactInformation } from './TicketContactInformation';
import { SubjectSiteFields } from './SubjectSiteFields';
import { TicketCategories } from './TicketCategories';
import { CreateTicketResponseBar } from './CreateTicketResponseBar';
import { useForm } from '../../../hooks/useForm';
import { useSupportTicket } from '../../../hooks/SupportTicket/useSupportTicket';
import { Loading } from '../../../components/Loading';

interface Props {
  onClose(): void;
  onCreateTicket(): void;
  open: boolean;
}

export const CreateTicketModal = ({ onClose, onCreateTicket, open }: Props) => {
  const [responseMessage, setResponseMessage] = useState<ResponseMessages>();
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ResponseMessages>();
  const {
    categories,
    createTicket,
    loading: createLoading,
    error,
    response,
    isSuccess,
  } = useSupportTicket();

  const { values, onChange, resetForm, updateField, onSubmit } =
    useForm<SupportCreateTicket>(
      {
        site: null,
        subject: '',
        category: null,
        name: '',
        phone: '',
        cc: '',
        text: '',
        attachment: null,
      },
      () => {
        setErrorMessage(undefined);
        setResponseMessage(undefined);
        createTicket(values);
        setTicketSubmitted(true);
      }
    );

  const handleClose = () => {
    setErrorMessage(undefined);
    setResponseMessage(undefined);
    setTicketSubmitted(false);
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (ticketSubmitted) {
      if (!createLoading && !error && isSuccess && response) {
        setResponseMessage({
          message: 'Ticket Submitted Successfully',
          subMessage: `Your ticket number is: ${
            response.DisplayID ?? response.ID ?? 'Unavailable'
          }`,
        });
        setTicketSubmitted(false);
        onCreateTicket();
      } else {
        if (error) {
          setTicketSubmitted(false);
          setErrorMessage({
            message: 'Error Submitting Ticket',
            subMessage: String(error.data || error.statusText),
          });
        }
      }
    }
  }, [
    createLoading,
    response,
    error,
    ticketSubmitted,
    isSuccess,
    onCreateTicket,
  ]);

  return (
    <Modal
      id="create-ticket-modal"
      header="Create New Ticket"
      headerIcon={<Create />}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      actionSuccess={!createLoading && Boolean(responseMessage)}
      actionMessages={{
        error: errorMessage ?? { message: '' },
        success: responseMessage ?? { message: '' },
      }}
      actionMessageTimeout={4000}
    >
      {createLoading ? (
        <>
          <div className="fixed inset-0 z-30">
            <Loading
              text={ticketSubmitted ? 'Creating Ticket...' : undefined}
            />
          </div>
          <div className="fixed bg-black/20 inset-0 z-20 backdrop-blur-[1px]" />
        </>
      ) : null}
      <ModalContent>
        {errorMessage ? (
          <p role="alert" className="p-2 text-error">
            {errorMessage.subMessage || errorMessage.message}
          </p>
        ) : null}
        <form className="flex flex-col w-full" onSubmit={onSubmit}>
          <div className="mt-[10px] max-h-[450px] p-2 overflow-auto">
            <TicketContactInformation
              ticketFields={values}
              loading={createLoading}
              onChange={onChange}
            />
            <SubjectSiteFields
              ticketFields={values}
              loading={createLoading}
              onChange={onChange}
              updateFormField={updateField}
            />
            <TicketCategories
              categories={categories}
              selectedCategory={values.category}
              loading={createLoading}
              updateFormField={updateField}
            />
          </div>
          <CreateTicketResponseBar
            ticketFields={values}
            loading={createLoading}
            onChange={onChange}
            updateFormField={updateField}
            onClose={handleClose}
          />
        </form>
      </ModalContent>
    </Modal>
  );
};
